/**
 * The relay's share-link answers as the device reads them (part 38d; part 38's routes, table LS): each read field by
 * field at the boundary (Definition of Done, security), anything else null, which the client counts a failure. What the
 * device sends is built here too, so every body leaves from one place and the request inspection (LW12) has one place to
 * hold to: the proof, never the secret; never the key.
 */
import { bytesOfLength, fromBase64Url, toBase64Url } from '../account/bytes.ts';
import { fieldsOf } from '../account/wire.ts';

/** The relay's link states (part 38's `LinkState`). */
export type LinkState = 'uploading' | 'live' | 'revoked' | 'expired' | 'disabled' | 'abandoned';

const LINK_STATES: readonly LinkState[] = ['uploading', 'live', 'revoked', 'expired', 'disabled', 'abandoned'];

/** Whether a link in `state` has ended (part 38's `hasEnded`). */
export function hasEnded(state: LinkState): state is Exclude<LinkState, 'uploading' | 'live'> {
  return state !== 'uploading' && state !== 'live';
}

/** A link's, an item's and a thread's ID: 16 bytes, base64url (part 38's `LINK_ID_BYTES`, `ITEM_ID_BYTES`, `THREAD_ID_BYTES`). */
export const SHARE_ID_BYTES = 16;

/** The most items a pull page holds (part 38's `ITEM_PAGE`), and the most IDs an acknowledgement names (`MAX_ACK_IDS`). */
export const ITEM_PAGE = 200;
export const MAX_ACK_IDS = 500;

/** The most bytes an item holds (part 38's `MAX_ITEM_BYTES`). */
export const MAX_ITEM_BYTES = 16 * 1024;

function stateOf(value: unknown): LinkState | null {
  return LINK_STATES.find((state) => state === value) ?? null;
}

function timeOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/** A time or null; undefined when it is neither. */
function timeOrNull(value: unknown): number | null | undefined {
  return value === null ? null : (timeOf(value) ?? undefined);
}

/** An ID of {@link SHARE_ID_BYTES} bytes, base64url; null otherwise. */
function idOf(value: unknown): string | null {
  return typeof value === 'string' && bytesOfLength(value, SHARE_ID_BYTES) !== null ? value : null;
}

/** An upload's place (part 38's `Upload`): the state, the parts stored and of how many. */
export interface Upload {
  readonly state: LinkState;
  readonly nextPart: number;
  readonly parts: number;
}

/** `{ state, nextPart, parts }`, or null. */
export function readUpload(value: unknown): Upload | null {
  const fields = fieldsOf(value);
  const state = stateOf(fields?.['state']);
  const nextPart = timeOf(fields?.['nextPart']);
  const parts = timeOf(fields?.['parts']);
  return state === null || nextPart === null || parts === null || parts < 1 || nextPart > parts ? null : { state, nextPart, parts };
}

/** Which cap stops a link's downloads (part 38's `Cap`). */
export type Cap = 'downloads' | 'egress' | null;

/** A link as the account's list shows it (part 38's `ListedLink`). */
export interface ListedLink {
  readonly id: string;
  readonly state: LinkState;
  readonly createdAt: number;
  readonly expiresAt: number | null;
  readonly endedAt: number | null;
  /** The operator's disable: why, and their note for the writer (decision 63). */
  readonly disabled: { readonly at: number; readonly reason: string | null; readonly note: string | null } | null;
  readonly servedKib: number;
  readonly capKib: number;
  readonly cap: Cap;
}

/** The account's links and its egress this month (part 38's `LinkList`). */
export interface LinkList {
  readonly links: readonly ListedLink[];
  readonly egress: { readonly kib: number; readonly capKib: number; readonly resetsAt: number };
}

function textOrNull(value: unknown): string | null | undefined {
  return value === null ? null : typeof value === 'string' ? value : undefined;
}

function disabledOf(value: unknown): ListedLink['disabled'] | undefined {
  if (value === null) return null;
  const fields = fieldsOf(value);
  const at = timeOf(fields?.['at']);
  const reason = textOrNull(fields?.['reason']);
  const note = textOrNull(fields?.['note']);
  return at === null || reason === undefined || note === undefined ? undefined : { at, reason, note };
}

function listedOf(value: unknown): ListedLink | null {
  const fields = fieldsOf(value);
  const id = idOf(fields?.['id']);
  const state = stateOf(fields?.['state']);
  const createdAt = timeOf(fields?.['createdAt']);
  const expiresAt = timeOrNull(fields?.['expiresAt']);
  const endedAt = timeOrNull(fields?.['endedAt']);
  const disabled = disabledOf(fields?.['disabled']);
  const servedKib = timeOf(fields?.['servedKib']);
  const capKib = timeOf(fields?.['capKib']);
  const rawCap = fields?.['cap'];
  const cap = rawCap === null || rawCap === 'downloads' || rawCap === 'egress' ? rawCap : undefined;
  if (id === null || state === null || createdAt === null || expiresAt === undefined || endedAt === undefined || disabled === undefined || servedKib === null || capKib === null || cap === undefined) return null;
  return { id, state, createdAt, expiresAt, endedAt, disabled, servedKib, capKib, cap };
}

/** `GET /v1/links`: `{ links, egress }`, or null. */
export function readLinkList(value: unknown): LinkList | null {
  const fields = fieldsOf(value);
  const raw = fields?.['links'];
  const egress = fieldsOf(fields?.['egress']);
  const kib = timeOf(egress?.['kib']);
  const capKib = timeOf(egress?.['capKib']);
  const resetsAt = timeOf(egress?.['resetsAt']);
  if (!Array.isArray(raw) || kib === null || capKib === null || resetsAt === null) return null;
  const links = raw.map(listedOf);
  return links.every((link): link is ListedLink => link !== null) ? { links, egress: { kib, capKib, resetsAt } } : null;
}

/** An item as a pull hands it (part 38's `Item`): its order, IDs, author, time and ciphertext. */
export interface PulledItem {
  readonly seq: number;
  readonly id: Uint8Array<ArrayBuffer>;
  readonly thread: Uint8Array<ArrayBuffer>;
  readonly author: 'reader' | 'writer';
  readonly at: number;
  readonly body: Uint8Array<ArrayBuffer>;
}

/** A pull's page: the link's state and end, its items, and whether more follow. */
export interface Pulled {
  readonly state: LinkState;
  readonly endedAt: number | null;
  readonly items: readonly PulledItem[];
  readonly more: boolean;
}

function itemOf(value: unknown): PulledItem | null {
  const fields = fieldsOf(value);
  const seq = timeOf(fields?.['seq']);
  const id = bytesOfLength(fields?.['id'], SHARE_ID_BYTES);
  const thread = bytesOfLength(fields?.['thread'], SHARE_ID_BYTES);
  const author = fields?.['author'];
  const at = timeOf(fields?.['at']);
  const raw = fields?.['body'];
  const body = typeof raw === 'string' ? fromBase64Url(raw) : null;
  if (seq === null || id === null || thread === null || (author !== 'reader' && author !== 'writer') || at === null || body === null || body.length === 0 || body.length > MAX_ITEM_BYTES) return null;
  return { seq, id, thread, author, at, body };
}

/** `POST /v1/links/pull`: `{ state, endedAt, items, more }`, at most a page of items in rising order, or null. */
export function readPulled(value: unknown): Pulled | null {
  const fields = fieldsOf(value);
  const state = stateOf(fields?.['state']);
  const endedAt = timeOrNull(fields?.['endedAt']);
  const raw = fields?.['items'];
  const more = fields?.['more'];
  if (state === null || endedAt === undefined || !Array.isArray(raw) || raw.length > ITEM_PAGE || typeof more !== 'boolean') return null;
  const items = raw.map(itemOf);
  if (!items.every((item): item is PulledItem => item !== null)) return null;
  // The next page starts after the last seq: an order that doesn't rise would pull a page again, or skip one.
  let last = -1;
  for (const item of items) {
    if (item.seq <= last) return null;
    last = item.seq;
  }
  return { state, endedAt, items, more };
}

/** `{ acknowledged }`, or null. */
export function readAcknowledged(value: unknown): number | null {
  return timeOf(fieldsOf(value)?.['acknowledged']);
}

/** `{ seq }` (a reply stored), or null. */
export function readReplied(value: unknown): number | null {
  return timeOf(fieldsOf(value)?.['seq']);
}

/** `{ state }` (a revoke), or null. */
export function readRevoked(value: unknown): LinkState | null {
  return stateOf(fieldsOf(value)?.['state']);
}

/** `{ revoked }` (revoke-all), or null. */
export function readRevokedAll(value: unknown): number | null {
  return timeOf(fieldsOf(value)?.['revoked']);
}

/** LW12: the owner's request body for `link` with proof `owner`: what the relay is sent of the secret is the proof alone. */
export function ownerBody(link: string, owner: Uint8Array, more: Readonly<Record<string, unknown>> = {}): Record<string, unknown> {
  return { link, owner: toBase64Url(owner), ...more };
}

/** `POST /v1/links`'s body (part 38's `CreateBody`): the ID, the proof, the sealed size, comments and the end. */
export function createBody(link: string, owner: Uint8Array, size: number, comments: boolean, expiresAt: number | null): Record<string, unknown> {
  return { link, owner: toBase64Url(owner), size, comments, expiresAt };
}

/** A snapshot part's frame (part 38's `partFrame`): one byte giving the header's length, the JSON header, the part's bytes. */
export function partFrame(link: string, index: number, bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const header = new TextEncoder().encode(JSON.stringify({ link, index }));
  const frame = new Uint8Array(1 + header.length + bytes.length);
  frame[0] = header.length;
  frame.set(header, 1);
  frame.set(bytes, 1 + header.length);
  return frame;
}
